import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { MultiPageVideoModel, ElementTypes } from '@bynder-studio/render-core';
import { List, Thumbnail, Flex, token } from '@bynder/design-system';
import { IconArrowLeft, IconTextStyle, IconArrowRight } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { parseSrt } from './parseSrt';
import TranscriptGenerateModal from './TranscriptGenerateModal';
import TranscriptStyles from './TranscriptStyles';
import TranscriptForm from './TranscriptForm';
import { getElementsFonts, getFonts } from 'packages/store/user/user.selectors';
import useForceUpdate from '~/hooks/useForceUpdate';
import { getDefaultFontId } from 'packages/pages/editor/utils/getDefaultFontId';
import { StyledDivider } from 'packages/pages/components/Transcript/Component.styled';

const defaultSrt = `1
00:00:00,000 --> 00:00:03,000
Welcome to the video

2
00:00:03,500 --> 00:00:07,000
This is an auto-generated
transcript for your video

3
00:00:07,500 --> 00:00:11,000
You can edit the text below`;

export const TranscriptListItem = ({
    creativeModel,
    onClick,
}: {
    creativeModel: MultiPageVideoModel;
    onClick: () => void;
}) => {
    const { translate } = useTranslate();

    return (
        <List.Item
            thumbnail={<Thumbnail variant="clean" icon={<IconTextStyle />} />}
            onClick={onClick}
            subtext={
                creativeModel?.getTranscriptElement?.().enabled
                    ? translate('editor.transcript.subtext.enabled')
                    : translate('editor.transcript.subtext.disabled')
            }
            rightElements={<IconArrowRight />}
        >
            {translate('editor.transcript.title')}
        </List.Item>
    );
};

export const TranscriptBackButton = ({ onBack }: { onBack: () => void }) => {
    const { translate } = useTranslate();

    return (
        <List>
            <List.Item
                onClick={onBack}
                thumbnail={
                    <Flex alignItems="center">
                        <StyledBackIcon />
                        <Thumbnail variant="clean" icon={<IconTextStyle />} />
                    </Flex>
                }
            >
                {translate('editor.transcript.title')}
            </List.Item>
        </List>
    );
};

const Transcript = ({
    creativeModel,
    onBack,
    showEnabledToggle = true,
    showStyles = true,
    showBackButton = true,
}: {
    creativeModel: MultiPageVideoModel;
    onBack: () => void;
    showEnabledToggle?: boolean;
    showStyles?: boolean;
    showBackButton?: boolean;
}) => {
    const { translate } = useTranslate();
    const [srtContent, setSrtContent] = useState('');
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const fonts = useSelector(getFonts);
    const elementsFonts = useSelector(getElementsFonts);
    const forceUpdate = useForceUpdate();

    const transcriptElement = creativeModel.getTranscriptElement();
    // todo: Delete after BE sync.
    const enabled = showEnabledToggle ? transcriptElement.enabled : true;

    const defaultFontId = useMemo(() => getDefaultFontId(elementsFonts, fonts), [elementsFonts, fonts]);

    const handleToggleEnabled = useCallback(() => {
        const defaultProperties = transcriptElement.fontId === null ? { fontId: defaultFontId } : {};

        creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
            enabled: !enabled,
            ...defaultProperties,
        });
        forceUpdate();
    }, [creativeModel, enabled, transcriptElement?.fontId, defaultFontId, forceUpdate]);

    const handleOpenGenerateModal = useCallback(() => {
        setShowGenerateModal(true);
    }, []);

    const handleCancelGenerate = useCallback(() => {
        setShowGenerateModal(false);
    }, []);

    const handleGenerate = useCallback(() => {
        setIsGenerating(true);

        // Simulate API response with default transcript
        setTimeout(() => {
            setSrtContent(defaultSrt);

            const cues = parseSrt(defaultSrt, creativeModel.getPlaybackDuration().frameRate);

            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                // Temporary while BE  doesn't have implementation
                enabled: true,
                cues,
                fontId: defaultFontId,
            });

            setIsGenerating(false);
            setShowGenerateModal(false);
            forceUpdate();
        }, 600);
    }, [creativeModel, defaultFontId, forceUpdate]);

    return (
        <>
            {showBackButton && <TranscriptBackButton onBack={onBack} />}
            {showEnabledToggle && (
                <List>
                    <List.Item checkedVariant="switch" isChecked={enabled} onClick={handleToggleEnabled}>
                        {translate('editor.transcript.enable')}
                    </List.Item>
                </List>
            )}
            {enabled && (
                <TranscriptForm
                    srtContent={srtContent}
                    isGenerating={isGenerating}
                    onGenerate={handleOpenGenerateModal}
                />
            )}
            {enabled && showStyles && (
                <>
                    <StyledDivider />
                    <TranscriptStyles creativeModel={creativeModel} />
                </>
            )}
            {showGenerateModal && (
                <TranscriptGenerateModal
                    isGenerating={isGenerating}
                    onGenerate={handleGenerate}
                    onCancel={handleCancelGenerate}
                />
            )}
        </>
    );
};

const StyledBackIcon = styled(IconArrowLeft)`
    margin-right: ${token.spacing3};
`;

export default Transcript;
